import { EnvironmentProviders, makeEnvironmentProviders } from '@angular/core';
import { ProviderFeature, providerFeature } from '@fmnts/common';
import { Iban, SortCodeWeightTable } from '@formunauts/banking/domain';
import { CountryBankData } from './bank-data/country-bank-data';
import { BankService } from './bank.service';
import {
  BANKS_COUNTRY_REGISTRY_TOKEN,
  IBAN_COUNTRY_REGISTRY_TOKEN,
  SORT_CODE_WEIGHT_TABLE,
} from './banking-data-acess.tokens';
import { IbanService } from './iban.service';
import { SortCodeService } from './sort-code.service';

export enum BankingFeatureKind {
  SortCodes,
  Ibans,
  Banks,
}

export type BankingFeature<TKind extends BankingFeatureKind> =
  ProviderFeature<TKind>;

const { make: makeBankingFeature } = providerFeature<BankingFeatureKind>();

export function provideBankingDataAccess(
  ...features: BankingFeature<BankingFeatureKind>[]
): EnvironmentProviders {
  const providers = features.flatMap((f) => f.providers);
  return makeEnvironmentProviders([...providers]);
}

/**
 * Add this feature to inherit the log event filters from the parent.
 */
export function withSortCodes(cfg: {
  weightTable: () => SortCodeWeightTable;
}): BankingFeature<BankingFeatureKind.SortCodes> {
  return makeBankingFeature(BankingFeatureKind.SortCodes, [
    SortCodeService,
    {
      provide: SORT_CODE_WEIGHT_TABLE,
      useFactory: cfg.weightTable,
    },
  ]);
}

/**
 * Add this feature to inherit the log event filters from the parent.
 */
export function WithIban(
  cfg: {
    ibanRegistry?: () => ReadonlyMap<string, Iban.IbanCountryMeta>;
  } = {},
): BankingFeature<BankingFeatureKind.Ibans> {
  const { ibanRegistry = () => new Map(Object.entries(Iban.registry)) } = cfg;
  return makeBankingFeature(BankingFeatureKind.Ibans, [
    IbanService,
    {
      provide: IBAN_COUNTRY_REGISTRY_TOKEN,
      useFactory: ibanRegistry,
    },
  ]);
}

/**
 * Add this feature to inherit the log event filters from the parent.
 */
export function withBanks(cfg: {
  bankRegistry: () => ReadonlyMap<string, CountryBankData>;
}): BankingFeature<BankingFeatureKind.Banks> {
  const { bankRegistry } = cfg;
  return makeBankingFeature(BankingFeatureKind.Banks, [
    BankService,
    {
      provide: BANKS_COUNTRY_REGISTRY_TOKEN,
      useFactory: bankRegistry,
    },
  ]);
}
